/**
 * Configuracion del agente de IA: prompt, contexto, modelo, temperatura,
 * idioma, horario de atencion y el interruptor general.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { AiSettingsView, UpdateAiSettingsRequest } from '@shared/api-types.ts';
import { api, errorMessage, isAbortError } from '../api.ts';
import type { NoticeKind } from '../hooks.ts';
import { WEEKDAY_LABELS } from '../state.ts';
import { ErrorBanner, Field, SkeletonBlock, Spinner } from './Feedback.tsx';
import { Icon } from './Icon.tsx';

type State =
  | { status: 'loading' }
  | { status: 'ready'; settings: AiSettingsView }
  | { status: 'error'; message: string };

const LANGUAGE_SUGGESTIONS = ['es', 'es-ES', 'en', 'pt', 'fr', 'it'];

function toPayload(settings: AiSettingsView): UpdateAiSettingsRequest {
  return {
    enabled: settings.enabled,
    systemPrompt: settings.systemPrompt,
    businessContext: settings.businessContext,
    model: settings.model,
    temperature: settings.temperature,
    maxOutputTokens: settings.maxOutputTokens,
    language: settings.language,
    businessHours: settings.businessHours,
  };
}

export function AiSettingsScreen({
  onNotice,
  canWrite,
}: {
  onNotice: (kind: NoticeKind, text: string) => void;
  /** Solo propietarios y administradores pueden guardar (la API responde 403). */
  canWrite: boolean;
}): ReactElement {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [form, setForm] = useState<AiSettingsView | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    api
      .getAiSettings(controller.signal)
      .then((settings) => {
        setState({ status: 'ready', settings });
        setForm(settings);
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setState({ status: 'error', message: errorMessage(error) });
      });
    return () => controller.abort();
  }, [reloadToken]);

  const original = state.status === 'ready' ? state.settings : null;
  const dirty = useMemo(() => {
    if (!form || !original) return false;
    return JSON.stringify(toPayload(form)) !== JSON.stringify(toPayload(original));
  }, [form, original]);

  const update = useCallback((patch: Partial<AiSettingsView>): void => {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const updateBusinessHours = useCallback(
    (patch: Partial<NonNullable<AiSettingsView['businessHours']>>): void => {
      setForm((prev) => {
        if (!prev) return prev;
        const base = prev.businessHours ?? { days: [1, 2, 3, 4, 5], open: '09:00', close: '18:00' };
        return { ...prev, businessHours: { ...base, ...patch } };
      });
    },
    [],
  );

  const handleSave = useCallback((): void => {
    if (!form || saving) return;
    setSaving(true);
    api
      .updateAiSettings(toPayload(form))
      .then((saved) => {
        setState({ status: 'ready', settings: saved });
        setForm(saved);
        onNotice('success', 'Configuracion del agente guardada.');
      })
      .catch((error: unknown) => onNotice('error', errorMessage(error)))
      .finally(() => setSaving(false));
  }, [form, onNotice, saving]);

  if (state.status === 'loading') {
    return (
      <div className="page">
        <header className="page__header">
          <h1 className="page__title">Configuracion del agente de IA</h1>
        </header>
        <SkeletonBlock height={90} />
        <SkeletonBlock height={200} />
        <SkeletonBlock height={160} />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="page">
        <header className="page__header">
          <h1 className="page__title">Configuracion del agente de IA</h1>
        </header>
        <ErrorBanner message={state.message} onRetry={() => setReloadToken((token) => token + 1)} />
      </div>
    );
  }

  if (!form) return <div className="page" />;

  const businessHours = form.businessHours;

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1 className="page__title">Configuracion del agente de IA</h1>
          <p className="page__subtitle">
            Define como responde el asistente cuando una conversacion esta en modo automatico.
          </p>
        </div>
        <div className="page__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              setForm(original);
            }}
            disabled={!dirty || saving || !canWrite}
          >
            Descartar cambios
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={handleSave}
            disabled={!dirty || saving || !canWrite}
          >
            {saving ? <Spinner label="Guardando…" /> : (<><Icon name="check" />Guardar</>)}
          </button>
        </div>
      </header>

      <div className="banner banner--info">
        <Icon name="info" className="banner__icon" />
        <p className="banner__text">
          El prompt define el tono y los limites del agente: que puede prometer, cuando debe pedir
          datos y cuando tiene que escalar a una persona. Escribelo en segunda persona y con reglas
          concretas; evita instrucciones vagas.
        </p>
      </div>

      {!canWrite ? (
        <div className="banner banner--warning" role="status">
          <Icon name="warning" className="banner__icon" />
          <p className="banner__text">
            Solo el propietario y los administradores de la organizacion pueden cambiar esta
            configuracion. Puedes consultarla, pero los cambios no se guardaran.
          </p>
        </div>
      ) : null}

      <fieldset className="fieldset" disabled={!canWrite}>
      <section className="card">
        <div className="card__row">
          <div>
            <h2 className="card__title">Estado del agente</h2>
            <p className="card__hint">
              Si lo desactivas, todas las conversaciones en modo automatico dejaran de recibir
              respuestas de la IA.
            </p>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(event) => update({ enabled: event.target.checked })}
            />
            <span className="switch__track" aria-hidden="true">
              <span className="switch__thumb" />
            </span>
            <span className="switch__label">{form.enabled ? 'Activado' : 'Desactivado'}</span>
          </label>
        </div>
      </section>

      <section className="card">
        <h2 className="card__title">Comportamiento</h2>

        <Field
          label="Prompt del sistema"
          htmlFor="ai-prompt"
          hint="Instrucciones permanentes del agente (tono, limites, datos que debe pedir). Maximo 8000 caracteres."
        >
          <textarea
            id="ai-prompt"
            className="input input--tall"
            rows={10}
            maxLength={8000}
            value={form.systemPrompt}
            onChange={(event) => update({ systemPrompt: event.target.value })}
          />
        </Field>

        <Field
          label="Contexto del negocio"
          htmlFor="ai-context"
          hint="Informacion de la empresa: productos, horarios, politicas de envio y devoluciones… Maximo 16000 caracteres."
        >
          <textarea
            id="ai-context"
            className="input input--tall"
            rows={6}
            maxLength={16000}
            value={form.businessContext}
            onChange={(event) => update({ businessContext: event.target.value })}
          />
        </Field>
      </section>

      <section className="card">
        <h2 className="card__title">Modelo</h2>

        <div className="grid grid--3">
          <Field label="Modelo" htmlFor="ai-model" hint="Modelos disponibles segun la configuracion del servidor.">
            {form.availableModels.length > 0 ? (
              <select
                id="ai-model"
                className="select"
                value={form.model}
                onChange={(event) => update({ model: event.target.value })}
              >
                {form.availableModels.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
                {form.availableModels.includes(form.model) ? null : (
                  <option value={form.model}>{form.model} (actual)</option>
                )}
              </select>
            ) : (
              <input
                id="ai-model"
                className="input"
                maxLength={100}
                value={form.model}
                onChange={(event) => update({ model: event.target.value })}
              />
            )}
          </Field>

          <Field
            label={`Temperatura: ${form.temperature.toFixed(1)}`}
            htmlFor="ai-temperature"
            hint="0 = respuestas mas predecibles; 2 = mas creativas."
          >
            <input
              id="ai-temperature"
              className="range"
              type="range"
              min={0}
              max={2}
              step={0.1}
              value={form.temperature}
              onChange={(event) => update({ temperature: Number(event.target.value) })}
            />
          </Field>

          <Field
            label="Maximo de tokens de salida"
            htmlFor="ai-tokens"
            hint="Limite de longitud de cada respuesta (entre 50 y 4000)."
          >
            <input
              id="ai-tokens"
              className="input"
              type="number"
              min={50}
              max={4000}
              step={50}
              value={form.maxOutputTokens}
              onChange={(event) => update({ maxOutputTokens: Number(event.target.value) })}
            />
          </Field>
        </div>

        <Field
          label="Idioma de las respuestas"
          htmlFor="ai-language"
          hint="Codigo de idioma (por ejemplo es o es-ES)."
        >
          <>
            <input
              id="ai-language"
              className="input"
              list="ai-language-options"
              maxLength={10}
              value={form.language}
              onChange={(event) => update({ language: event.target.value })}
            />
            <datalist id="ai-language-options">
              {LANGUAGE_SUGGESTIONS.map((language) => (
                <option key={language} value={language} />
              ))}
            </datalist>
          </>
        </Field>
      </section>

      <section className="card">
        <div className="card__row">
          <div>
            <h2 className="card__title">Horario de atencion</h2>
            <p className="card__hint">
              Fuera de este horario el agente puede avisar al cliente o escalar la conversacion.
            </p>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={businessHours !== null}
              onChange={(event) =>
                update({
                  businessHours: event.target.checked
                    ? { days: [1, 2, 3, 4, 5], open: '09:00', close: '18:00', outsideMessage: '' }
                    : null,
                })
              }
            />
            <span className="switch__track" aria-hidden="true">
              <span className="switch__thumb" />
            </span>
            <span className="switch__label">{businessHours ? 'Definido' : 'Sin horario'}</span>
          </label>
        </div>

        {businessHours ? (
          <>
            <Field label="Dias de atencion">
              <div className="days">
                {WEEKDAY_LABELS.map((label, index) => (
                  <label className="days__item" key={label}>
                    <input
                      type="checkbox"
                      checked={businessHours.days.includes(index)}
                      onChange={(event) => {
                        const days = event.target.checked
                          ? [...businessHours.days, index].sort((a, b) => a - b)
                          : businessHours.days.filter((day) => day !== index);
                        updateBusinessHours({ days });
                      }}
                    />
                    <span>{label.slice(0, 3)}</span>
                  </label>
                ))}
              </div>
            </Field>

            <div className="grid grid--2">
              <Field label="Apertura" htmlFor="ai-open">
                <input
                  id="ai-open"
                  className="input"
                  type="time"
                  value={businessHours.open}
                  onChange={(event) => updateBusinessHours({ open: event.target.value })}
                />
              </Field>
              <Field label="Cierre" htmlFor="ai-close">
                <input
                  id="ai-close"
                  className="input"
                  type="time"
                  value={businessHours.close}
                  onChange={(event) => updateBusinessHours({ close: event.target.value })}
                />
              </Field>
            </div>

            <Field
              label="Mensaje fuera de horario"
              htmlFor="ai-outside"
              hint="Se envia cuando el cliente escribe fuera del horario configurado."
            >
              <textarea
                id="ai-outside"
                className="input"
                rows={3}
                maxLength={1000}
                value={businessHours.outsideMessage ?? ''}
                onChange={(event) => updateBusinessHours({ outsideMessage: event.target.value })}
              />
            </Field>
          </>
        ) : null}
      </section>
      </fieldset>
    </div>
  );
}
